import React, { useEffect, useRef, useState } from "react";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../hook/use-req";
import { COUNTRY_CONFIGS } from "../audience-upload/data";
import { SingleAudienceResponse } from "../../../../pages/campaign/types";
import { CampaignData } from "../../../types";

interface ManualTabProps {
  channel: string;
  isEmailCampaign: boolean;
  onManualSuccess: (audienceId: string, validRecords: number) => void;
  audienceById: SingleAudienceResponse | null;
  campaignData: CampaignData;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onValidation: (isValid: boolean) => void;
}

interface ContactRecord {
  sno: number;
  phone: string;
  email: string;
  firstName: string;
  lastName: string;
  status: "valid" | "invalid" | "duplicate";
}

interface ProcessedManualData {
  id: number;
  name: string;
  uploadDate: string;
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  duplicateRecords: number;
  preview: ContactRecord[];
  data: ContactRecord[];
  countryCounts?: Record<string, number>;
}

const ManualTab: React.FC<ManualTabProps> = ({
  channel,
  isEmailCampaign,
  onManualSuccess,
  audienceById,
  setCampaignData,
  onValidation,
}) => {
  const token = localStorage.getItem("authToken");

  const [manualText, setManualText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("NG");
  const [processedManualData, setProcessedManualData] = useState<ProcessedManualData | null>(null);
  const [isUploadingManual, setIsUploadingManual] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [uploadedAudienceId, setUploadedAudienceId] = useState<string | null>(null);
  const manualTextareaRef = useRef<HTMLTextAreaElement>(null);
  const uploadInProgress = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const { makeRequest: uploadManualAudience } = useRequest("/audience/upload/list", "POST", {
    Authorization: `Bearer ${token}`,
  });

  // Function to detect country from international format
  const detectCountryFromNumber = (phone: string): string | null => {
    const cleanPhone = phone.replace(/\D/g, "");

    for (const [countryCode, config] of Object.entries(COUNTRY_CONFIGS)) {
      const numericCode = config.code.replace("+", "");
      if (cleanPhone.startsWith(numericCode)) {
        return countryCode;
      }
    }

    return null;
  };

  // Function to add country code to a phone number
  const formatPhoneWithCountryCode = (
    phone: string,
    countryCode: string
  ): string => {
    const cleanPhone = phone.replace(/\D/g, "");
    const config = COUNTRY_CONFIGS[countryCode as keyof typeof COUNTRY_CONFIGS];

    if (!config) return phone;

    const numericCountryCode = config.code.replace("+", "");

    if (cleanPhone.startsWith(numericCountryCode)) {
      return `+${cleanPhone}`;
    }

    if (config.localPrefix && cleanPhone.startsWith(config.localPrefix)) {
      return `${config.code}${cleanPhone.slice(config.localPrefix.length)}`;
    }

    const localNumber = cleanPhone.replace(
      new RegExp(`^${numericCountryCode}`),
      ""
    );
    if (config.pattern.test(localNumber) || config.pattern.test(cleanPhone)) {
      return `${config.code}${localNumber || cleanPhone}`;
    }

    return phone;
  };

  const validatePhoneNumber = (phone: string, countryCode?: string) => {
    const cleanPhone = phone.replace(/\D/g, "");

    const detectedCountry = detectCountryFromNumber(phone);
    const targetCountry = detectedCountry || countryCode || selectedCountry;
    const config =
      COUNTRY_CONFIGS[targetCountry as keyof typeof COUNTRY_CONFIGS];

    if (!config) return false;

    const numericCountryCode = config.code.replace("+", "");

    if (cleanPhone.startsWith(numericCountryCode)) {
      const localPart = cleanPhone.slice(numericCountryCode.length);
      return config.pattern.test(localPart);
    }

    if (config.localPrefix && cleanPhone.startsWith(config.localPrefix)) {
      const withoutPrefix = cleanPhone.slice(config.localPrefix.length);
      return config.pattern.test(withoutPrefix);
    }

    return config.pattern.test(cleanPhone);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Function to process contacts from audienceById
  const processExistingContacts = () => {
    if (!audienceById?.contacts || audienceById.contacts.length === 0) {
      return false;
    }

    // Convert contacts to text format
    const contactsText = audienceById.contacts.join('\n');
    setManualText(contactsText);

    // Process the contacts
    const lines = contactsText.split("\n").filter(line => line.trim());

    const results: ProcessedManualData = {
      id: Date.now(),
      name: "Manual Entry",
      uploadDate: new Date().toISOString().split("T")[0],
      totalRecords: lines.length,
      validRecords: 0,
      invalidRecords: 0,
      duplicateRecords: 0,
      preview: [],
      data: [],
    };

    const seenEntries = new Set<string>();
    const processedData: ContactRecord[] = [];
    const countryCounts: Record<string, number> = {};

    lines.forEach((rawEntry, index) => {
      let status: "valid" | "invalid" | "duplicate" = "valid";

      if (isEmailCampaign) {
        // Email validation logic
        if (!validateEmail(rawEntry)) {
          status = "invalid";
          results.invalidRecords++;
        } else {
          const normalizedEmail = rawEntry.trim().toLowerCase();

          if (seenEntries.has(normalizedEmail)) {
            status = "duplicate";
            results.duplicateRecords++;
          } else {
            seenEntries.add(normalizedEmail);
            results.validRecords++;
          }

          const record: ContactRecord = {
            sno: index + 1,
            phone: "",
            email: normalizedEmail,
            firstName: `Contact ${index + 1}`,
            lastName: "",
            status,
          };

          processedData.push(record);
          if (results.preview.length < 5) results.preview.push(record);
        }
      } else {
        // Phone number validation logic
        let detectedCountry = detectCountryFromNumber(rawEntry) || selectedCountry;

        if (!validatePhoneNumber(rawEntry)) {
          status = "invalid";
          results.invalidRecords++;
        } else {
          const formattedPhone = formatPhoneWithCountryCode(
            rawEntry,
            detectedCountry
          );

          if (seenEntries.has(formattedPhone)) {
            status = "duplicate";
            results.duplicateRecords++;
          } else {
            seenEntries.add(formattedPhone);
            results.validRecords++;

            countryCounts[detectedCountry] =
              (countryCounts[detectedCountry] || 0) + 1;
          }

          const record: ContactRecord = {
            sno: index + 1,
            phone: formattedPhone,
            email: "",
            firstName: `Contact ${index + 1}`,
            lastName: "",
            status,
          };

          processedData.push(record);
          if (results.preview.length < 5) results.preview.push(record);
        }
      }
    });

    results.data = processedData;
    if (!isEmailCampaign) {
      results.countryCounts = countryCounts;
    }

    setProcessedManualData(results);
    setHasUploaded(true);
    setUploadedAudienceId(audienceById.audience_id);
    uploadInProgress.current = true;

    // Call the success callback with the existing audience data
    onManualSuccess(audienceById.audience_id, results.validRecords);

    // Validate the data for parent component
    const isValid = results.validRecords > 0;
    onValidation(isValid);

    return true;
  };

  // Load existing data from audienceById when component mounts
  useEffect(() => {
    if (!isInitialized && audienceById?.type === "manual" && audienceById?.contacts && audienceById.contacts.length > 0) {
      setIsInitialized(true);
      processExistingContacts();
    }
  }, [audienceById, isInitialized]);

  // Update validation when processedManualData changes
  useEffect(() => {
    // Notify parent about validation status
    const isValid = !!processedManualData && processedManualData.validRecords > 0;
    onValidation(isValid);
  }, [processedManualData, onValidation]);

  const setManualEntries = (value: string) => {
    const entries = value.split("\n").map((line) => ({ raw: line }));

    setCampaignData((prev) => ({
      ...prev,
      typedNumbers: entries,
    }));
  };

  const handleProcessManualEntries = () => {
    const lines = manualText.split("\n").filter(line => line.trim());

    if (lines.length === 0) {
      toast.error(`Please enter ${isEmailCampaign ? "email addresses" : "phone numbers"}`);
      return;
    }

    if (lines.length > 1000) {
      toast.error(`Maximum 1000 ${isEmailCampaign ? "email addresses" : "phone numbers"} allowed`);
      return;
    }

    const results: ProcessedManualData = {
      id: Date.now(),
      name: "Manual Entry",
      uploadDate: new Date().toISOString().split("T")[0],
      totalRecords: lines.length,
      validRecords: 0,
      invalidRecords: 0,
      duplicateRecords: 0,
      preview: [],
      data: [],
    };

    const seenEntries = new Set<string>();
    const processedData: ContactRecord[] = [];
    const countryCounts: Record<string, number> = {};

    lines.forEach((rawEntry, index) => {
      let status: "valid" | "invalid" | "duplicate" = "valid";

      if (isEmailCampaign) {
        // Email validation logic
        if (!validateEmail(rawEntry)) {
          status = "invalid";
          results.invalidRecords++;
        } else {
          const normalizedEmail = rawEntry.trim().toLowerCase();

          if (seenEntries.has(normalizedEmail)) {
            status = "duplicate";
            results.duplicateRecords++;
          } else {
            seenEntries.add(normalizedEmail);
            results.validRecords++;
          }

          const record: ContactRecord = {
            sno: index + 1,
            phone: "",
            email: normalizedEmail,
            firstName: `Contact ${index + 1}`,
            lastName: "",
            status,
          };

          processedData.push(record);
          if (results.preview.length < 5) results.preview.push(record);
        }
      } else {
        // Phone number validation logic
        let detectedCountry = detectCountryFromNumber(rawEntry) || selectedCountry;

        if (!validatePhoneNumber(rawEntry)) {
          status = "invalid";
          results.invalidRecords++;
        } else {
          const formattedPhone = formatPhoneWithCountryCode(
            rawEntry,
            detectedCountry
          );

          if (seenEntries.has(formattedPhone)) {
            status = "duplicate";
            results.duplicateRecords++;
          } else {
            seenEntries.add(formattedPhone);
            results.validRecords++;

            countryCounts[detectedCountry] =
              (countryCounts[detectedCountry] || 0) + 1;
          }

          const record: ContactRecord = {
            sno: index + 1,
            phone: formattedPhone,
            email: "",
            firstName: `Contact ${index + 1}`,
            lastName: "",
            status,
          };

          processedData.push(record);
          if (results.preview.length < 5) results.preview.push(record);
        }
      }
    });

    results.data = processedData;
    if (!isEmailCampaign) {
      results.countryCounts = countryCounts;
    }
    setProcessedManualData(results);
    setHasUploaded(false);
    setUploadedAudienceId(null);
    uploadInProgress.current = false;

    if (results.validRecords === 0) {
      toast.error(`No valid ${isEmailCampaign ? "email addresses" : "phone numbers"} found`);
    } else {
      toast.success(`Found ${results.validRecords} valid ${isEmailCampaign ? "email addresses" : "phone numbers"}`);
    }
  };

  const handleUploadManualAudience = async () => {
    // Prevent multiple uploads using ref and state
    if (uploadInProgress.current || isUploadingManual || hasUploaded || uploadedAudienceId) {
      console.log("Upload already in progress or completed");
      return;
    }

    if (!processedManualData || !processedManualData.data) {
      toast.error(`Please validate ${isEmailCampaign ? "email addresses" : "numbers"} first`);
      return;
    }

    // Extract valid entries from processed data
    const validContacts = processedManualData.data
      .filter((contact) => contact.status === "valid")
      .map((contact) => (isEmailCampaign ? contact.email : contact.phone));

    if (validContacts.length === 0) {
      toast.error(`No valid ${isEmailCampaign ? "email addresses" : "phone numbers"} to upload`);
      return;
    }

    uploadInProgress.current = true;
    setIsUploadingManual(true);

    try {
      // Prepare the data for API request
      const requestData = {
        contacts: validContacts,
      };

      // Make the API request
      const [response] = await uploadManualAudience(requestData);

      if (response && (response.audience_id || response.id)) {
        const audienceId = response?.audience_id || response?.id;

        // Store the uploaded audience ID
        setUploadedAudienceId(audienceId);
        localStorage.setItem("audienceId", String(audienceId));
        localStorage.setItem("validRecords", String(processedManualData.validRecords));

        // Set the flag to prevent multiple calls
        setHasUploaded(true);

        toast.success("Contacts uploaded successfully");

        // Call the success callback only once
        onManualSuccess(audienceId, processedManualData.validRecords);

        // Clear manual data after successful upload
        setManualText("");
        setProcessedManualData(null);
      } else {
        toast.error("Failed to upload contacts");
        uploadInProgress.current = false;
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload contacts");
      uploadInProgress.current = false;
    } finally {
      setIsUploadingManual(false);
      // Don't reset uploadInProgress.current here if successful - keep it true to prevent re-upload
      if (!uploadedAudienceId) {
        uploadInProgress.current = false;
      }
    }
  };

  const clearManualData = () => {
    setManualText("");
    setProcessedManualData(null);
    setHasUploaded(false);
    setUploadedAudienceId(null);
    uploadInProgress.current = false;
  };

  // Helper function to get status color and icon
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "valid":
        return { color: "text-green-600", bg: "bg-green-50", icon: CheckCircle, text: "Valid" };
      case "invalid":
        return { color: "text-red-600", bg: "bg-red-50", icon: XCircle, text: "Invalid" };
      case "duplicate":
        return { color: "text-yellow-600", bg: "bg-yellow-50", icon: AlertCircle, text: "Duplicate" };
      default:
        return { color: "text-gray-600", bg: "bg-gray-50", icon: AlertCircle, text: "Unknown" };
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        {/* Country Selection - Only show for SMS campaigns */}
        {!isEmailCampaign && (
          <div className="mb-4 flex justify-between items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Country (for numbers without country code)
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.entries(COUNTRY_CONFIGS).map(([code, config]) => (
                  <option key={code} value={code}>
                    {config.code} {config.name}
                  </option>
                ))}
              </select>
            </div>

            {manualText && (
              <button
                type="button"
                onClick={clearManualData}
                className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>
        )}

        <div className="space-y-4">
          <textarea
            ref={manualTextareaRef}
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder={
              isEmailCampaign
                ? `Enter email addresses (one per line)`
                : `Enter phone numbers (one per line)`
            }
            rows={6}
            className="w-full px-4 py-3 h-[56px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
          />

          <div className="flex justify-between items-center text-sm flex-wrap gap-4">
            <span className="text-gray-600">
              {isEmailCampaign ? "Emails" : "Numbers"} entered:{" "}
              {manualText.split("\n").filter(line => line.trim()).length || 0}
            </span>
            <span className="text-gray-600">Limit: 1000</span>
          </div>

          <button
            type="button"
            onClick={handleProcessManualEntries}
            disabled={!manualText.trim()}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isEmailCampaign ? "Validate Emails" : "Validate Numbers"}
          </button>
        </div>
      </div>


      {processedManualData && (
        <div className="space-y-6">
          <div className="flex justify-center gap-4 flex-wrap">
            <button
              type="button"
              onClick={handleUploadManualAudience}
              disabled={
                processedManualData.validRecords === 0 ||
                isUploadingManual ||
                hasUploaded ||
                !!uploadedAudienceId
              }
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploadingManual ? (
                "Uploading..."
              ) : hasUploaded || uploadedAudienceId ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Uploaded ({processedManualData.validRecords} contacts)
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Save Audience ({processedManualData.validRecords} contacts)
                </>
              )}
            </button>

            <button
              type="button"
              onClick={clearManualData}
              className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              disabled={isUploadingManual}
            >
              Clear & Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualTab;